import React, {useEffect, useState} from 'react';
import SuggestionButton from './SuggestionButton';
import {tradlyCopilot} from '../Actions/tradlyCopilot';
import MessageContent from './messages/MessageContent';
import {aiPromptMixPanelEvent} from '../Models/MixPannelEnum';
import SettingsModal from './SettingsModal';
import AppConstants from '../Constants/AppConstants';

const CopilotPanel = ({isOpen, onClose}) => {
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState('');

  // Close on escape key
  useEffect(() => {
    const handleEscape = e => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Get system prompt from localStorage
  useEffect(() => {
    const savedPrompt = localStorage.getItem('tradly_ai_system_prompt');
    setSystemPrompt(savedPrompt);
  }, []);

  const handleSendMessage = async text => {
    const messageText = text || prompt;
    if (!messageText.trim()) return;

    // Add user message to chat
    setMessages(prev => [
      ...prev,
      {
        type: 'user',
        text: messageText,
      },
    ]);

    // Clear input if it's from input field
    if (!text) setPrompt('');

    // Show loading state
    setIsLoading(true);

    try {
      const response = await tradlyCopilot({
        prompt: messageText,
        systemPrompt: systemPrompt,
      });

      // Add response to chat
      if (response.data.result) {
        aiPromptMixPanelEvent({status: 'success', prompt: messageText});

        setMessages(prev => [
          ...prev,
          {
            type: 'assistant',
            text: response.message || 'Success! Response received.',
            data: response.data.result,
          },
        ]);
      } else {
        aiPromptMixPanelEvent({status: 'error', prompt: messageText});

        setMessages(prev => [
          ...prev,
          {
            type: 'error',
            text: `We couldn't generate a result this time — we're working to make Iceflow AI better every day. Feel free to try again or tweak your prompt.`,
          },
        ]);
      }
    } catch (error) {
      aiPromptMixPanelEvent({status: 'error', prompt: messageText});

      // Add error message to chat
      setMessages(prev => [
        ...prev,
        {
          type: 'error',
          text: `We couldn't generate a result this time — we're working to make Iceflow AI better every day. Feel free to try again or tweak your prompt.`,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestionClick = suggestion => {
    handleSendMessage(suggestion);
  };

  const handleKeyPress = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/30 transition-opacity duration-300
                   ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={`fixed top-0 right-0 w-[420px] h-full bg-white shadow-2xl 
                   transform transition-transform duration-300 ease-out flex flex-col
                   ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="copilot-title">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center w-8 h-8 bg-indigo-50 rounded-lg">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                className="w-5 h-5 text-primary"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round">
                <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
                <path d="M20 3v4" />
                <path d="M22 5h-4" />
                <path d="M4 17v2" />
                <path d="M5 18H3" />
              </svg>
            </span>
            <h2
              id="copilot-title"
              className="text-lg font-semibold flex items-center gap-2">
              Tradly AI
              <span className="text-xs px-1.5 py-0.5 bg-indigo-100 text-primary rounded-md font-medium">
                Beta
              </span>
            </h2>
          </div>
          {AppConstants.isInternal && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsSettingsOpen(true)}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg
                       hover:bg-gray-100 transition-colors">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg
                       hover:bg-gray-100 transition-colors">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            {messages.length === 0 ? (
              // Initial view with suggestions
              <>
                <h3 className="text-xl font-semibold mb-2 text-center">
                  How can I help you today?
                </h3>
                <p className="text-gray-600 mb-8 text-center text-sm">
                  Ask me to create content, analyze data, or perform quick
                  actions
                </p>

                {/* Suggestions */}
                <div className="space-y-2.5">
                  <SuggestionButton
                    onClick={() =>
                      handleSuggestionClick('Show me top selling products')
                    }>
                    Show me top selling products
                  </SuggestionButton>
                  <SuggestionButton
                    onClick={() =>
                      handleSuggestionClick(
                        'Create a new collection with latest listings',
                      )
                    }>
                    Create a new collection with latest listings
                  </SuggestionButton>
                  <SuggestionButton
                    onClick={() =>
                      handleSuggestionClick("What's my revenue trend?")
                    }>
                    What's my revenue trend?
                  </SuggestionButton>
                </div>
              </>
            ) : (
              // Chat messages
              <div className="space-y-4">
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex ${
                      message.type === 'user' ? 'justify-end' : 'justify-start'
                    }`}>
                    <div
                      className={`max-w-[95%] rounded-lg ${
                        message.type === 'user'
                          ? 'bg-primary text-white px-4 py-2'
                          : message.type === 'error'
                          ? 'bg-red-50 text-red-600 px-4 py-2'
                          : 'bg-transparent'
                      }`}>
                      <MessageContent
                        message={message}
                        onAction={message => {
                          setMessages(prev => [...prev, message]);
                          if (message.type === 'user') {
                            setIsLoading(true);
                          } else {
                            setIsLoading(false);
                          }
                        }}
                      />
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="flex items-center gap-2 text-gray-500">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:0.2s]" />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:0.4s]" />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Input Area */}
        <div className="p-4 border-t bg-gray-50">
          <div className="relative">
            <textarea
              type="text"
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask anything..."
              className="w-full pl-4 pr-12 py-3 bg-white rounded-lg border border-gray-200
                       focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500
                       transition-all outline-none"
              rows={4}
            />
            <button
              onClick={() => handleSendMessage()}
              disabled={isLoading || !prompt.trim()}
              className="absolute right-2 bottom-2   p-1.5
                       text-gray-400 hover:text-primary transition-colors
                       disabled:opacity-50 disabled:cursor-not-allowed">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 5l7 7-7 7M5 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Add Settings Modal */}
      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        systemPrompt={systemPrompt}
        setSystemPrompt={setSystemPrompt}
      />
    </>
  );
};

export default CopilotPanel;
